import { Button } from "@bednbody/ui/components/button";
import { ArrowClockwiseIcon, WarningIcon } from "@phosphor-icons/react";

/**
 * ⚠ THE DEFECT THIS EXISTS FOR: a failed read on this page rendered as GOOD
 * NEWS.
 *
 * Every card here takes `isLoading` and no card takes `isError`. TanStack Query
 * answers a failure with `isLoading: false` and `data: undefined`, each card
 * coalesces that to `[]`, and the empty branch renders. So an unreachable
 * backend — or an RLS refusal, or an expired session — produced
 * "Riders available now: 0" above "Nothing waiting for dispatch. 🎉", and a
 * dispatcher was told the floor was clear at the exact moment they could not
 * see it.
 *
 * That is the only defect on the dashboard that states the OPPOSITE of the
 * truth rather than merely omitting it, which is why it is worth a control of
 * its own.
 *
 * ⚠ IT ANNOUNCES, IT DOES NOT HIDE. The cards below still render whatever did
 * load — a dashboard that blanks itself because one of nine queries failed is
 * worse than one that qualifies itself. This band is the qualification: the
 * numbers underneath are no longer allowed to read as complete.
 *
 * The per-card `isError` wiring is the fuller fix and is NOT done — twenty
 * components would each need the prop threaded through. This closes the
 * dangerous half now.
 */
export function StaleDataBanner({
  failed,
  onRetry,
  isRetrying,
}: {
  /** True when ANY query feeding this page is in error. */
  failed: boolean;
  onRetry: () => void;
  isRetrying?: boolean;
}) {
  if (!failed) return null;

  return (
    <section
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4"
    >
      <WarningIcon size={20} weight="fill" className="shrink-0 text-destructive" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-destructive">Some live data didn&apos;t load</p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Figures and queues below may be incomplete or out of date. An empty card here does not
          mean there is nothing to do — check again before acting on it.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry} disabled={isRetrying}>
        <ArrowClockwiseIcon weight="bold" />
        {isRetrying ? "Retrying…" : "Retry"}
      </Button>
    </section>
  );
}
